import { useState, useEffect, useRef, useCallback, useReducer } from 'react'

interface AuthImageProps {
  /** Pre-resolved signed URL or public URL for the media. */
  src: string
  alt: string
  className?: string
  /** Controls when the download starts. 'lazy' (default) waits until near viewport. */
  loading?: 'lazy' | 'eager'
  isVideo?: boolean
  /** Called once when the media has fully loaded and painted. */
  onLoad?: () => void
  /** Set to false to suppress the skeleton placeholder (when the parent handles its own loading UI). */
  skeleton?: boolean
  /** Forwarded to the underlying <img> so PhotoSwipe can query it by item ID. */
  'data-item-id'?: string
  /** Element Timing attribute — enables Sentry elementTimingIntegration metrics. */
  elementtiming?: string
  /** Wait for full image decode (all progressive scans) before calling onLoad. */
  waitForDecode?: boolean
}

/**
 * Renders an image or video from a pre-resolved URL.
 *
 * The outer div takes className directly from the caller (which must supply
 * sizing + positioning, e.g. "absolute inset-0"). The skeleton is rendered as
 * the div's own background color so it requires no child positioning — avoiding
 * all the absolute/relative stacking conflicts. The image/video is absolutely
 * positioned within the caller's already-positioned container.
 *
 * Lazy mode (default): waits until within 200px of viewport before loading.
 * Eager mode: loads immediately (use for above-the-fold images).
 */
export default function AuthImage({ src, alt, className, loading = 'lazy', isVideo = false, onLoad, skeleton = true, 'data-item-id': dataItemId, elementtiming, waitForDecode }: AuthImageProps) {
  const [activeSrc, setActiveSrc] = useState<string | null>(loading === 'eager' && src ? src : null)
  const [loaded, setLoaded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  // retryCount bumps the IntersectionObserver effect so it re-fires after an error
  const [, forceRetry] = useReducer((n: number) => n + 1, 0)

  // When src changes, update activeSrc for eager mode.
  // Never reset loaded=false here: if the image is already painted, it should stay visible
  // while the new URL loads (which is near-instant from cache). The onLoad/onLoadedData
  // handlers are responsible for setting loaded=true after a real fetch.
  // loaded is only ever reset to false on initial mount (useState default).
  useEffect(() => {
    if (!src) return
    if (loading === 'eager') {
      setActiveSrc(src)
    }
  }, [src, loading])

  // Safari on iOS won't render a poster frame unless we seek explicitly
  const handleLoadedMetadata = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
    e.currentTarget.currentTime = 0.001
  }, [])

  // On load error (expired signed URL, network blip, 403, etc.) reset activeSrc
  // back to null so the skeleton re-appears, then bump forceRetry to re-run the
  // IntersectionObserver effect — which will immediately re-fire for visible
  // elements and set activeSrc to the current src prop for a fresh attempt.
  const handleError = useCallback(() => {
    setActiveSrc(null)
    setLoaded(false)
    forceRetry()
  }, [forceRetry])

  // Intersection observer: only start loading when near the viewport.
  // Also re-runs after forceRetry increments (triggered by onError) so a
  // failed load can re-attempt once the element is back in view.
  useEffect(() => {
    if (loading === 'eager') return
    if (!src) return

    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setActiveSrc(src)
          observer.disconnect()
        }
      },
      { rootMargin: '9999px 0px 200px 0px' }
    )

    observer.observe(el)
    return () => observer.disconnect()
    // forceRetry is intentionally included: bumping it resets activeSrc to null
    // (via handleError below) then re-runs this effect to re-observe the element.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, loading, forceRetry])

  return (
    <div ref={containerRef} className={`${className} ${skeleton && !loaded ? 'bg-gray-900 overflow-hidden' : ''}`}>
      {/* Shimmer sweep: visible during both waiting and downloading phases */}
      {skeleton && !loaded && (
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none" />
      )}
      {activeSrc && (isVideo ? (
        <video
          src={activeSrc}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          muted
          playsInline
          preload="metadata"
          onLoadedMetadata={handleLoadedMetadata}
          onLoadedData={() => { setLoaded(true); onLoad?.() }}
          onError={handleError}
        />
      ) : (
        <img
          src={activeSrc}
          alt={alt}
          data-item-id={dataItemId}
          {...({
            fetchpriority: loading === 'eager' ? 'high' : undefined,
            elementtiming: elementtiming || undefined,
          } as React.HTMLAttributes<HTMLImageElement>)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={async (e) => {
            setLoaded(true)
            if (waitForDecode) {
              const img = e.currentTarget
              try { await img.decode() } catch { /* decode can reject on broken images */ }
            }
            onLoad?.()
          }}
          onError={handleError}
        />
      ))}
    </div>
  )
}
